import useUser from 'utils/useUser';
import { useMemo, useState, type FC } from 'react';
import {
  useAddActivityComment,
  useGenAiComment,
} from 'infrastructure/hooks/rpm';
import { useAppSelector } from 'store';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { isCombinedModule } from 'infrastructure/functions';
import { useDeleteActivityComment } from 'infrastructure/hooks/rpm/activity/use-delete-comment';
import { ActivityStatus, type ModuleType } from 'infrastructure/enums';
import { useGetDoNotCallInfo } from 'infrastructure/hooks/control-panel/use-get-call-info';
import BaseIconButton from 'components/atoms/base-icon-button';
import LinkedReadingCallsIcon from 'components/atoms/icons/linked-readings-call';
import LinkedReadingCommentIcon from 'components/atoms/icons/linked-readings-comment';
import { formatAuthorName } from 'utils/formatters';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import CommentsColumn from './components/comments-col';
import CommentColumn from './components/comment-col';
import ReviewedCheckbox from './components/reviewed-checkbox';
import s from './styles.module.scss';
import ErroneousWrapper from './components/erroneous-modal';
import LinkedCalls from './components/linkedCalls';

import type { TView } from './types';
import type { QueryKey } from '@tanstack/react-query';
import type {
  IActivityData,
  TCommentCellViewMode,
} from 'infrastructure/interfaces/i-rpm-patient-activity';

interface CommentCellProps {
  createdAt: string;
  record: IActivityData;
  idx: number | string;
  getActivityQueryKey: QueryKey;
  view: TView;
  module: ModuleType;
  type?: ActivityStatus;
  status?: ActivityStatus;
  loading: boolean;
}

const CommentCell: FC<CommentCellProps> = ({
  idx,
  record,
  getActivityQueryKey,
  view,
  module,
  type,
  status,
  loading,
}) => {
  const { doNotCallRpm } = useGetDoNotCallInfo();

  const {
    userType,
    hasClinicalRole,
    isFullyManagedAgency,
    isAtRiskRole,
    isAgencyDoctor,
  } = useUser();
  const { isReadOnlyRole } = useCheckRoleAccess();
  const [viewMode, setViewMode] = useState<TCommentCellViewMode>('comment');
  const { t } = useTranslation();

  const isReviewed = useMemo(() => {
    const reviewedByUserType = record.comments.find(
      (c) => c.reviewed,
    )?.userType;
    return reviewedByUserType === userType && record.reviewed;
  }, [record.comments.length]);
  const actions = useAppSelector((state) => state.activityActions.actions[idx]);

  const { addActivityComment, InteractionStatusAlert } = useAddActivityComment({
    getActivityQueryKey,
    patientGuid: record.patient.guid,
    tableRowIdx: idx,
  });

  const { deleteActivityComment } = useDeleteActivityComment({
    getActivityQueryKey,
    patientGuid: record.patient.guid,
    tableRowIdx: idx,
  });

  const {
    data: aiData,
    genAiComment,
    isCommentGenerationLimitReached,
    isLoading,
  } = useGenAiComment({
    patientGuid: record.patient.guid,
    tableRowIdx: idx,
  });

  const submitCommentLockByAi =
    Boolean(actions?.message) &&
    aiData?.result.comment === actions?.message &&
    hasClinicalRole;

  const showErroneousBtnForAdminView = view === 'admin' && status === undefined;
  const showErroneousBtnForPatientView =
    view === 'patient' && status && status !== ActivityStatus.Erroneous;
  const showErroneousBtn =
    showErroneousBtnForAdminView || showErroneousBtnForPatientView;
  const hideReviewCheckbox =
    status === ActivityStatus.Actioned ||
    status === ActivityStatus.Erroneous ||
    status === ActivityStatus.UnactionedNormalRisk;
  const showReviewCheckbox = !record.isErroneous && !hideReviewCheckbox;
  const showCommentColumn = isCombinedModule(module) ? !isReadOnlyRole : true;
  const showReviewColumn = showCommentColumn;

  // This is temporary solution, delete it after new dashboard release
  const disableForFullyManagedAgency =
    view === 'dashboard' &&
    isFullyManagedAgency &&
    isAgencyDoctor &&
    isAtRiskRole;

  const hideCommentArea =
    view === 'patient' &&
    isFullyManagedAgency &&
    isAgencyDoctor &&
    type === ActivityStatus.UnactionedNormalRisk;

  const handleSwitchMod = () => {
    if (viewMode === 'comment') setViewMode('linked-calls');
    if (viewMode === 'linked-calls') setViewMode('comment');
  };

  return (
    <>
      <div className={s['comments-cell']}>
        <div className={s['comments-wrapper']}>
          <CommentsColumn
            deleteComment={deleteActivityComment}
            comments={record.comments}
            patientGuid={record.patient.guid}
            erroneousReason={record.erroneousReason}
            erroneousMarkedAt={record.erroneousMarkedAt}
            erroneousAuthorName={record.erroneousAuthorName}
            isErroneous={record.isErroneous}
          />
          {viewMode === 'comment' && showCommentColumn && !hideCommentArea && (
            <CommentColumn
              addComment={addActivityComment}
              genAiComment={genAiComment}
              deviceId={record.deviceId}
              readingId={record.id}
              idx={idx}
              key={`comment-col-${idx}`}
              createdAt={record.createdAt}
              showAiButton={status !== ActivityStatus.Erroneous}
              currentInteractionStatus={record.patient.interactionStatus}
              patientGuid={record.patient.guid}
              genAiCommentLoading={isLoading}
              isCommentGenerationLimitReached={isCommentGenerationLimitReached}
              aiData={aiData}
              isErroneous={record.isErroneous}
              thresholds={record.patient.threshold}
              values={record.value}
              statuses={record.statuses}
              readingType={record.readingType}
              globalStatus={record.globalStatus}
              thresholdName={record.thresholdName}
              disabled={disableForFullyManagedAgency}
              patientRpmDoNotCall={doNotCallRpm}
            />
          )}
          {viewMode === 'linked-calls' && (
            <LinkedCalls
              linkedCalls={record.linkedCalls ?? []}
              patientGuid={record.patient.guid}
              patientName={formatAuthorName(record.patient)}
              patientPhone={record.patient.phone}
              globalStatus={record.globalStatus}
              readingType={record.readingType}
              statuses={record.statuses}
              thresholds={record.patient.threshold}
              values={record.value}
              key={`reading-col-${idx}`}
              thresholdName={record.thresholdName}
              patientTimezone={record.patient.timezone}
            />
          )}
          {showReviewColumn && (
            <div className={s.reviewed}>
              {showErroneousBtn && (
                <ErroneousWrapper
                  getActivityQueryKey={getActivityQueryKey}
                  deviceId={record.deviceId}
                  readingId={record.id}
                  isErroneous={loading ? false : record.isErroneous}
                />
              )}
              {view !== 'dashboard' && (
                <BaseIconButton
                  icon={
                    viewMode === 'comment' ? (
                      <LinkedReadingCallsIcon />
                    ) : (
                      <LinkedReadingCommentIcon />
                    )
                  }
                  title={
                    viewMode === 'comment'
                      ? t('linkedCallReadings.switchesToLinkedCallsMode')
                      : t('linkedCallReadings.switchesToCommentMode')
                  }
                  className={classNames(s['linked-call-btn'], {
                    [s['move-top']]:
                      showErroneousBtnForAdminView ||
                      !showErroneousBtnForPatientView,
                  })}
                  onClick={handleSwitchMod}
                />
              )}
              {showReviewCheckbox && (
                <div className={s['checkbox-wrapper']}>
                  <ReviewedCheckbox
                    key={record.id + record.deviceId}
                    addComment={addActivityComment}
                    deviceId={record.deviceId}
                    readingId={record.id}
                    idx={idx}
                    currentInteractionStatus={record.patient.interactionStatus}
                    isReviewed={isReviewed}
                    submitCommentLockByAi={submitCommentLockByAi}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {InteractionStatusAlert}
    </>
  );
};
export default CommentCell;
